import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, getFilteredRowModel, flexRender } from '@tanstack/react-table';
import ViewListIcon from '@material-ui/icons/ViewList';
import AppsIcon from '@material-ui/icons/Apps';
import { Modal as BootstrapModal } from 'react-bootstrap';
import styled from 'styled-components';
import { colorAccent, colorBackgroundBody, colorBorder, colorText, colorWhite, colorHover, colorGreen, colorRed } from '../../theme/palette';
import Icon from '../Icon';
import TableLoading from './components/TableLoading';
import ReactTablePagination from '../table/components/ReactTablePagination';
import { useListagemConfiguracaoContextOptional } from '../ListagemConfiguracao/ListagemConfiguracaoContext';
import { notification } from '../../utils/notification';

const MAX_LOADING_ROWS = 10;
const MAX_LOADING_COLUMNS = 12;

/**
 * Função para determinar a cor da linha baseada na prioridade
 * Prioridade: Coleta > Negociação > Vazio > DescargaPendente
 */

const getRowColor = row => {
  if (row?.rowColor) {
    return row.rowColor;
  }

  const coleta = row?.coleta === 'Sim';
  const negociacao = row?.negociacao === 'Sim';
  const vazio = row?.vazio === 'Sim';
  const descargaPendente = row?.descargaPendente === 'Sim'; // Prioridade: Coleta > Negociação > Vazio > DescargaPendente

  if (coleta) {
    return '#e8f5e8'; // Verde opaco
  } else if (negociacao) {
    return '#fff3e0'; // Laranja opaco
  } else if (vazio) {
    return '#fffde7'; // Amarelo opaco
  } else if (descargaPendente) {
    return '#ffebee'; // Vermelho opaco
  }

  return 'transparent'; // Sem cor
};

/**
 * Componente TanStack Table reutilizável
 *
 * @param {Object} props
 * @param {Array} props.columns - Definição das colunas
 * @param {Array} props.data - Dados da tabela
 * @param {Function} props.onRowClick - Função chamada ao clicar na linha
 * @param {boolean} props.isSortable - Se a tabela é ordenável
 * @param {boolean} props.withPagination - Se deve mostrar paginação
 * @param {Array} props.pageSizeOptions - Opções de tamanho de página
 * @param {Object} props.initialState - Estado inicial da tabela
 * @param {string} props.className - Classe CSS adicional
 * @param {boolean} props.enableRowColors - Se deve habilitar coloração das linhas
 * @param {Function} props.rowColorGetter - Cor de fundo por linha (substitui a regra padrão de enableRowColors); retorne null para sem cor
 * @param {boolean} props.autoResetPageIndex - false mantém a página atual quando os dados mudam (padrão do TanStack: volta para a primeira)
 * @param {boolean} props.loading - Estado de carregamento
 * @param {boolean} props.enableRowSelection - Se deve habilitar seleção de linhas
 * @param {Function} props.onSelectionChange - Função chamada quando a seleção muda
 * @param {Array} props.selectedRows - Linhas selecionadas
 * @param {boolean} props.manualPagination - Se a paginação é controlada pelo servidor
 * @param {number} props.totalItems - Total de registros (para paginação manual)
 * @param {Function} props.onPaginationChange - Callback ao mudar página/tamanho (para paginação manual)
 * @param {{pageIndex:number,pageSize:number}} props.controlledPagination - Estado de paginação controlado externamente
 */


const TanStackTable = ({
  columns,
  data,
  onRowClick,
  isSortable = true,
  withPagination = true,
  pageSizeOptions = [5, 10, 50, 100],
  initialState = {},
  className,
  enableRowColors = false,
  loading = false,
  enableRowSelection = false,
  onSelectionChange,
  selectedRows = [],
  getRowId,
  manualPagination = false,
  totalItems = 0,
  onPaginationChange,
  controlledPagination,
  enableColumnConfig = false,
  columnConfigKey: columnConfigKeyProp,
  enableExportExcel = false,
  exportFileName,
  rowTitleGetter,
  rowColorGetter,
  autoResetPageIndex,
  enableColumnResizing = false,
  columnResizeMode = 'onChange',
  columnResizeDirection = 'ltr',
  enableCardView = false,
  defaultViewMode = 'table',
  renderCard,
  cardEmptyMessage = 'Nenhum registro encontrado',
  enableCopy = false,
  onColumnLayoutChange: onColumnLayoutChangeProp,
  onColumnSizingChange,
  copyButtonPosition = 'end'
}) => {
  const listagemCtx = useListagemConfiguracaoContextOptional();
  const columnConfigKey = columnConfigKeyProp ?? listagemCtx?.columnConfigKey;
  const onColumnLayoutChange = onColumnLayoutChangeProp ?? listagemCtx?.onColumnLayoutChange;
  // Com a configuracao de listagem (FilterPanel) ativa, a persistencia de layout
  // e feita exclusivamente via "Salvar configuracao" (servidor); o localStorage
  // e ignorado para nao sobrescrever a configuracao aplicada.
  const isListagemConfigAtiva = listagemCtx?.enabled === true;
  const [sorting, setSorting] = useState(initialState.sorting || []);
  const [columnVisibility, setColumnVisibility] = useState(initialState.columnVisibility || {});
  const [columnOrder, setColumnOrder] = useState(initialState.columnOrder || []);
  const [columnPinning, setColumnPinning] = useState(initialState.columnPinning || {});
  const [columnSizing, setColumnSizing] = useState(initialState.columnSizing || {});
  const [pagination, setPagination] = useState({
    pageIndex: initialState.pageIndex || 0,
    pageSize: initialState.pageSize || pageSizeOptions[0]
  });
  const [rowSelection, setRowSelection] = useState({});
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showExportConfig, setShowExportConfig] = useState(false);
  const [exportColumnIds, setExportColumnIds] = useState([]);
  const [hasStoredConfig, setHasStoredConfig] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const [viewMode, setViewMode] = useState(defaultViewMode);
  const configListRef = React.useRef(null);
  const lastNotifiedLayoutRef = React.useRef(null);
  const initialColumnStateRef = React.useRef({
    columnVisibility: initialState.columnVisibility || {},
    columnOrder: initialState.columnOrder || [],
    columnPinning: initialState.columnPinning || {},
    columnSizing: initialState.columnSizing || {}
  });
  const resolveRowId = useCallback((row, index, parent) => {
    if (typeof getRowId === 'function') {
      return String(getRowId(row, index, parent));
    }

    const fallbackId = row?.id ?? row?.Id ?? index;
    return String(fallbackId);
  }, [getRowId]);
  const dataByRowId = useMemo(() => {
    const map = new Map();
    data.forEach((item, index) => {
      map.set(resolveRowId(item, index), item);
    });
    return map;
  }, [data, resolveRowId]);
  const computedPageCount = useMemo(() => {
    if (!withPagination || !manualPagination) return undefined;
    const size = pagination.pageSize || pageSizeOptions[0];
    return Math.max(1, Math.ceil((totalItems || 0) / size));
  }, [withPagination, manualPagination, totalItems, pagination.pageSize, pageSizeOptions]);
  const table = useReactTable({
    data,
    columns,
    getRowId: resolveRowId,
    // Permite manter a página atual quando os dados são atualizados (ex.: edição inline).
    ...(autoResetPageIndex !== undefined && {
      autoResetPageIndex
    }),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(withPagination && !manualPagination && {
      getPaginationRowModel: getPaginationRowModel()
    }),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    ...(enableColumnResizing && {
      onColumnSizingChange: setColumnSizing
    }),
    ...(withPagination && {
      onPaginationChange: updater => {
        let next;

        if (typeof updater === 'function') {
          next = updater(pagination);
        } else if (typeof updater === 'number') {
          next = { ...pagination,
            pageIndex: updater
          };
        } else if (updater && typeof updater === 'object') {
          next = { ...pagination,
            ...updater
          };
        } else {
          next = pagination;
        } // Evitar loops e chamadas desnecessárias quando não há mudança real


        const isSamePage = next.pageIndex === pagination.pageIndex && next.pageSize === pagination.pageSize;

        if (isSamePage) {
          return;
        }

        setPagination(next);

        if (manualPagination && typeof onPaginationChange === 'function') {
          onPaginationChange(next);
        }
      }
    }),
    ...(enableRowSelection && {
      onRowSelectionChange: setRowSelection,
      enableRowSelection: true
    }),
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      columnPinning,
      ...(enableColumnResizing && {
        columnSizing
      }),
      ...(withPagination && {
        pagination
      }),
      ...(enableRowSelection && {
        rowSelection
      })
    },
    enableSorting: isSortable,
    enablePinning: true,
    enableColumnResizing: !!enableColumnResizing,
    columnResizeMode,
    columnResizeDirection,
    manualPagination: !!manualPagination,
    manualSorting: false,
    manualFiltering: false,
    ...(manualPagination && withPagination ? {
      pageCount: computedPageCount
    } : {})
  }); // Sincronizar paginação interna com estado controlado do pai

  useEffect(() => {
    if (!enableColumnConfig || !columnConfigKey) return;
    if (isListagemConfigAtiva) return;
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      const stored = localStorage.getItem(columnConfigKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setColumnVisibility(parsed?.columnVisibility || {});
        setColumnOrder(parsed?.columnOrder || []);
        setColumnPinning(parsed?.columnPinning || {});
        setColumnSizing(parsed?.columnSizing || {});
        setHasStoredConfig(true);
      }
    } catch (error) {
      console.warn('Falha ao carregar configuracao de colunas:', error);
    }
  }, [enableColumnConfig, columnConfigKey, isListagemConfigAtiva]);

  useEffect(() => {
    if (!enableColumnConfig || typeof onColumnLayoutChange !== 'function') {
      return;
    }

    const columnIds = columns
      .map((column) => column.id || column.accessorKey)
      .filter(Boolean);

    const nextLayout = {
      columnVisibility,
      columnOrder,
      columnSizing,
      columnIds
    };

    const serializedLayout = JSON.stringify(nextLayout);
    if (lastNotifiedLayoutRef.current === serializedLayout) {
      return;
    }

    lastNotifiedLayoutRef.current = serializedLayout;
    onColumnLayoutChange(nextLayout);
  }, [columnVisibility, columnOrder, columnSizing, columns, enableColumnConfig, onColumnLayoutChange]);

  useEffect(() => {
    if (!enableColumnConfig) return;
    const leafIds = table.getAllLeafColumns().map(column => column.id);
    setColumnOrder(prevOrder => {
      if (!prevOrder || prevOrder.length === 0) return prevOrder;
      const filtered = prevOrder.filter(id => leafIds.includes(id));
      const missing = leafIds.filter(id => !filtered.includes(id));
      const nextOrder = [...filtered, ...missing];
      if (nextOrder.length === prevOrder.length && nextOrder.every((value, index) => value === prevOrder[index])) {
        return prevOrder;
      }
      return nextOrder;
    });
  }, [enableColumnConfig, columns]);

  useEffect(() => {
    if (!enableColumnResizing) return;
    const leafIds = new Set(table.getAllLeafColumns().map(column => column.id));
    setColumnSizing(prevSizing => {
      const nextSizing = Object.entries(prevSizing || {}).reduce((acc, [columnId, size]) => {
        if (leafIds.has(columnId)) {
          acc[columnId] = size;
        }

        return acc;
      }, {});
      const prevKeys = Object.keys(prevSizing || {});
      const nextKeys = Object.keys(nextSizing);

      if (prevKeys.length === nextKeys.length && prevKeys.every(key => nextSizing[key] === prevSizing[key])) {
        return prevSizing;
      }

      return nextSizing;
    });
  }, [enableColumnResizing, columns, table]);

  // Avisa quem usa a tabela quando o usuário muda a largura (ex.: para salvar a preferência).
  // Não avisa a largura inicial; com columnResizeMode="onEnd" o aviso vem uma vez, ao soltar.
  const lastNotifiedSizingRef = React.useRef(JSON.stringify(initialState.columnSizing || {}));
  useEffect(() => {
    if (!enableColumnResizing || typeof onColumnSizingChange !== 'function') return;
    const serialized = JSON.stringify(columnSizing || {});
    if (serialized === lastNotifiedSizingRef.current) return;
    lastNotifiedSizingRef.current = serialized;
    onColumnSizingChange(columnSizing || {});
  }, [columnSizing, enableColumnResizing, onColumnSizingChange]);

  React.useEffect(() => {
    if (manualPagination && controlledPagination) {
      const next = {
        pageIndex: controlledPagination.pageIndex ?? pagination.pageIndex,
        pageSize: controlledPagination.pageSize ?? pagination.pageSize
      }; // Evitar loops desnecessários

      if (next.pageIndex !== pagination.pageIndex || next.pageSize !== pagination.pageSize) {
        setPagination(next);
      }
    }
  }, [manualPagination, controlledPagination?.pageIndex, controlledPagination?.pageSize]); // Notificar mudanças na seleção

  React.useEffect(() => {
    if (enableRowSelection && onSelectionChange) {
      const selectedRowIds = Object.keys(rowSelection);
      const selectedData = selectedRowIds
        .map(rowId => dataByRowId.get(rowId))
        .filter(Boolean); // Só chamar onSelectionChange se os dados realmente mudaram

      const currentSelection = JSON.stringify(selectedData.map((item, index) => resolveRowId(item, index)));
      const previousSelection = JSON.stringify(selectedRows.map((item, index) => resolveRowId(item, index)));

      if (currentSelection !== previousSelection) {
        onSelectionChange(selectedData);
      }
    }
  }, [rowSelection, dataByRowId, enableRowSelection, onSelectionChange, selectedRows, resolveRowId]);

  useEffect(() => {
    setViewMode(defaultViewMode);
  }, [defaultViewMode]);

  const handleRowClick = row => {
    if (onRowClick) {
      onRowClick(row.original);
    }
  }; // Mostrar loading se estiver carregando

  const getCellCopyText = useCallback(cell => {
    const copyValue = cell?.column?.columnDef?.copyValue;

    if (typeof copyValue === 'function') {
      const customValue = copyValue(cell.getContext());
      return customValue === null || customValue === undefined ? '' : String(customValue);
    }

    const rawValue = typeof cell?.getValue === 'function'
      ? cell.getValue()
      : cell?.row?.getValue?.(cell.column.id);

    if (rawValue === null || rawValue === undefined) return '';
    if (Array.isArray(rawValue)) return rawValue.join(', ');
    if (typeof rawValue === 'object') return '';

    return String(rawValue);
  }, []);

  const handleCopyCell = useCallback(async (event, cell) => {
    event.stopPropagation();

    const textToCopy = getCellCopyText(cell).trim();

    if (!textToCopy) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      notification.success('Copiado!');
    } catch (error) {
      notification.error('Nao foi possivel copiar.');
    }
  }, [getCellCopyText]);


  const handleSaveColumnConfig = () => {
    if (!enableColumnConfig || !columnConfigKey) return;
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      localStorage.setItem(columnConfigKey, JSON.stringify({
        columnVisibility,
        columnOrder,
        columnPinning,
        columnSizing
      }));
      setHasStoredConfig(true);
      setShowColumnConfig(false);
    } catch (error) {
      console.warn('Falha ao salvar configuracao de colunas:', error);
    }
  };

  const handleResetColumnConfig = () => {
    if (!enableColumnConfig || !columnConfigKey) return;
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(columnConfigKey);
    }
    setHasStoredConfig(false);
    setColumnVisibility(initialColumnStateRef.current.columnVisibility || {});
    setColumnOrder(initialColumnStateRef.current.columnOrder || []);
    setColumnPinning(initialColumnStateRef.current.columnPinning || {});
    setColumnSizing(initialColumnStateRef.current.columnSizing || {});
  };

  const getColumnLabel = column => {
    const header = column.columnDef?.header;
    if (typeof header === 'string') return header;
    return column.id;
  };

  const moveColumn = useCallback((columnId, direction) => {
    const currentOrder = columnOrder && columnOrder.length
      ? [...columnOrder]
      : table.getAllLeafColumns().map(column => column.id);
    const currentIndex = currentOrder.indexOf(columnId);
    if (currentIndex === -1) return;
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= currentOrder.length) return;
    const nextOrder = [...currentOrder];
    const [moved] = nextOrder.splice(currentIndex, 1);
    nextOrder.splice(targetIndex, 0, moved);
    setColumnOrder(nextOrder);
  }, [columnOrder, table]);

  const handleDragStart = useCallback((event, columnId) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', columnId);
  }, []);

  const handleDragOver = useCallback((event, columnId) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (columnId && columnId !== dragOverId) {
      setDragOverId(columnId);
    }
  }, [dragOverId]);

  const handleDrop = useCallback(
    (event, targetColumnId) => {
      event.preventDefault();
      setDragOverId(null);
      const sourceId = event.dataTransfer.getData('text/plain');
      if (!sourceId || sourceId === targetColumnId) return;

      const currentOrder = columnOrder && columnOrder.length
        ? [...columnOrder]
        : table.getAllLeafColumns().map(column => column.id);
      const sourceIndex = currentOrder.indexOf(sourceId);
      const targetIndex = currentOrder.indexOf(targetColumnId);
      if (sourceIndex === -1 || targetIndex === -1) return;

      const nextOrder = [...currentOrder];
      const [moved] = nextOrder.splice(sourceIndex, 1);
      nextOrder.splice(targetIndex, 0, moved);
      setColumnOrder(nextOrder);
    },
    [columnOrder, table]
  );

  const handleDragLeave = useCallback((event, columnId) => {
    if (dragOverId === columnId) {
      setDragOverId(null);
    }
  }, [dragOverId]);

  const handleAutoScroll = useCallback((event) => {
    const container = configListRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const threshold = 48;
    const scrollStep = 10;
    if (event.clientY < rect.top + threshold) {
      container.scrollTop -= scrollStep;
    } else if (event.clientY > rect.bottom - threshold) {
      container.scrollTop += scrollStep;
    }
  }, []);

  const handleExportExcel = useCallback(async (selectedIds) => {
    if (!enableExportExcel) return;
    try {
      const mod = await import('xlsx');
      const XLSX = mod?.default ?? mod;
      if (!XLSX?.utils || !XLSX?.writeFile) {
        console.warn('Biblioteca XLSX indisponivel para exportacao.');
        return;
      }

      const visibleColumns = table.getVisibleLeafColumns();
      const selected = Array.isArray(selectedIds) && selectedIds.length > 0
        ? selectedIds
        : visibleColumns.map(column => column.id);
      const columnsMeta = visibleColumns.filter(column => selected.includes(column.id)).map(column => ({
        id: column.id,
        header: typeof column.columnDef?.header === 'string' ? column.columnDef.header : column.id
      }));

      const rows = table.getRowModel().rows;
      const exportRows = rows.map(row => {
        const record = {};
        columnsMeta.forEach(column => {
          record[column.header] = row.getValue(column.id);
        });
        return record;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados');
      const baseFileName = exportFileName || 'exportacao.xlsx';
      const fileName = baseFileName.toLowerCase().endsWith('.xlsx')
        ? baseFileName
        : `${baseFileName}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.warn('Falha ao exportar Excel:', error);
    }
  }, [enableExportExcel, exportFileName, table]);

  const handleOpenExportConfig = useCallback(() => {
    const visibleIds = table.getVisibleLeafColumns().map(column => column.id);
    setExportColumnIds(visibleIds);
    setShowExportConfig(true);
  }, [table]);

  const handleToggleExportColumn = useCallback((columnId) => {
    setExportColumnIds(prev => (
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    ));
  }, []);

  const handleExportConfirm = useCallback(async () => {
    await handleExportExcel(exportColumnIds);
    setShowExportConfig(false);
  }, [handleExportExcel, exportColumnIds]);

  const getPinningStyles = column => {
    const pinned = column.getIsPinned();
    if (!pinned) return {};
    const isLastLeft = pinned === 'left' && column.getIsLastColumn('left');
    const isFirstRight = pinned === 'right' && column.getIsFirstColumn('right');
    return {
      position: 'sticky',
      left: pinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: pinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      zIndex: pinned ? 15 : 0,
      boxShadow: isLastLeft
        ? '2px 0 6px rgba(0,0,0,0.12)'
        : isFirstRight
          ? '-2px 0 6px rgba(0,0,0,0.12)'
          : undefined
      ,backgroundClip: 'padding-box'
    };
  };

  if (loading) {
    const loadingRows = Math.min(pagination.pageSize || pageSizeOptions[0] || 5, MAX_LOADING_ROWS);
    const loadingColumns = Math.min(
      table.getVisibleLeafColumns().length || columns.length || MAX_LOADING_COLUMNS,
      MAX_LOADING_COLUMNS
    );
    return <TableLoading rows={loadingRows} columns={loadingColumns} />;
  }

  const canRenderCards = enableCardView && typeof renderCard === 'function';
  const showToolbar = enableColumnConfig || enableExportExcel || canRenderCards;
  const rowModelRows = withPagination ? table.getRowModel().rows : table.getSortedRowModel().rows;
  const pageState = table.getState().pagination;
  const shouldApplyManualFallbackSlice =
    manualPagination &&
    withPagination &&
    rowModelRows.length > (pageState?.pageSize || pageSizeOptions[0] || 10);
  const visibleRows = shouldApplyManualFallbackSlice
    ? rowModelRows.slice(
        (pageState?.pageIndex || 0) * (pageState?.pageSize || pageSizeOptions[0] || 10),
        ((pageState?.pageIndex || 0) + 1) * (pageState?.pageSize || pageSizeOptions[0] || 10)
      )
    : rowModelRows;

  return <div className={className}>
      {showToolbar && <ConfigToolbar>
          <ToolbarGroup>
            {canRenderCards && <ViewModeSwitch>
                <ViewModeButton
                  type="button"
                  $active={viewMode === 'cards'}
                  onClick={() => setViewMode('cards')}
                  aria-label="Visualizacao em cards"
                >
                  <AppsIcon fontSize="small" />
                </ViewModeButton>
                <ViewModeButton
                  type="button"
                  $active={viewMode === 'table'}
                  onClick={() => setViewMode('table')}
                  aria-label="Visualizacao em tabela"
                >
                  <ViewListIcon fontSize="small" />
                </ViewModeButton>
              </ViewModeSwitch>}
            {enableExportExcel && <ConfigButton type="button" onClick={handleOpenExportConfig}>
                Exportar
              </ConfigButton>}
          </ToolbarGroup>
          <ToolbarGroup>
            {enableColumnConfig && columnConfigKey && <>
                <ConfigButton type="button" onClick={() => setShowColumnConfig(true)}>
                  Configurar colunas
                </ConfigButton>
                {hasStoredConfig && <ConfigHint>Layout personalizado ativo</ConfigHint>}
              </>}
          </ToolbarGroup>
        </ConfigToolbar>}
      {canRenderCards && viewMode === 'cards' ? (
        <CardsContainer>
          {visibleRows.length > 0 ? visibleRows.map((row, index) => (
            <CardItem key={row.id}>
              {renderCard(row.original, {
                row,
                index
              })}
            </CardItem>
          )) : (
            <CardEmptyState>{cardEmptyMessage}</CardEmptyState>
          )}
        </CardsContainer>
      ) : <TableContainer withPagination={withPagination}>
        <Table
          style={enableColumnResizing ? {
            width: table.getTotalSize(),
            minWidth: '100%'
          } : undefined}
        >
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => <TableHeaderCell key={header.id} style={{
              width: header.getSize(),
              minWidth: header.getSize(),
              ...getPinningStyles(header.column)
            }} onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined} sortable={header.column.getCanSort()} sticky={header.column.columnDef.sticky}>
                    <HeaderContent resizable={enableColumnResizing && header.column.getCanResize()}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && <SortIcon>
                          {{
                    asc: '↑',
                    desc: '↓'
                  }[header.column.getIsSorted()] ?? '↕'}
                        </SortIcon>}
                    </HeaderContent>
                    {enableColumnResizing && header.column.getCanResize() && <ColumnResizer
                      onClick={event => event.stopPropagation()}
                      onDoubleClick={event => {
                        event.stopPropagation();
                        header.column.resetSize();
                      }}
                      onMouseDown={event => {
                        event.stopPropagation();
                        header.getResizeHandler()(event);
                      }}
                      onTouchStart={event => {
                        event.stopPropagation();
                        header.getResizeHandler()(event);
                      }}
                      className={header.column.getIsResizing() ? 'isResizing' : ''}
                      style={{
                        transform: columnResizeMode === 'onEnd' && header.column.getIsResizing()
                          ? `translateX(${(columnResizeDirection === 'rtl' ? -1 : 1) * (table.getState().columnSizingInfo?.deltaOffset ?? 0)}px)`
                          : undefined
                      }}
                    />}
                  </TableHeaderCell>)}
              </TableRow>)}
          </TableHeader>
          <TableBody>
            {visibleRows.length > 0 ? visibleRows.map(row => {
            const rowColor = typeof rowColorGetter === 'function' ? rowColorGetter(row.original) : enableRowColors ? getRowColor(row.original) : null;
            const rowTextColor = row.original?.rowTextColor;
            const stripeColor = row.index % 2 === 0 ? '#f4f6fa' : '#f8f9fa';
            const customRowColor = rowColor && rowColor !== 'transparent' ? rowColor : null;
            const rowBackground = customRowColor || undefined;
            const pinnedBackground = customRowColor || stripeColor;
            const rowTitle = typeof rowTitleGetter === 'function' ? rowTitleGetter(row.original) : undefined;
            return <TableRow key={row.id} onClick={event => {
              if (onRowClick) {
                onRowClick(row.original, event);
              }
            }} clickable={!!onRowClick} style={{
              backgroundColor: rowBackground,
              color: rowTextColor
            }} title={rowTitle}>
                    {row.getVisibleCells().map(cell => {
                const columnCopyEnabled = cell.column.columnDef.enableCopy !== false;
                const copyText = enableCopy && columnCopyEnabled ? getCellCopyText(cell) : '';
                const canCopy = Boolean(copyText.trim());
                const copyButton = canCopy ? <CopyCellButton
                    $start={copyButtonPosition === 'start'}
                    type="button"
                    title="Copiar valor"
                    aria-label="Copiar valor da celula"
                    onClick={event => handleCopyCell(event, cell)}
                  >
                    <Icon name="content-copy" size={14} />
                  </CopyCellButton> : null;

                return <TableCell key={cell.id} sticky={cell.column.columnDef.sticky} style={{
                  width: cell.column.getSize(),
                  minWidth: cell.column.getSize(),
                  backgroundColor: cell.column.getIsPinned() ? pinnedBackground : rowBackground,
                  color: rowTextColor,
                  ...getPinningStyles(cell.column)
                }}>
                        <CellInner $copyStart={copyButtonPosition === 'start'}>
                          {copyButtonPosition === 'start' && copyButton}
                          <CellValueWrap>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </CellValueWrap>
                          {copyButtonPosition !== 'start' && copyButton}
                        </CellInner>
                      </TableCell>;
              })}
                  </TableRow>;
          }) : <TableRow>
                <EmptyCell colSpan={columns.length}>
                <EmptyMessage>
                  Nenhum registro encontrado
                </EmptyMessage>
              </EmptyCell>
            </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>}

      {withPagination && visibleRows.length > 0 && <ReactTablePagination dataLength={manualPagination ? totalItems : table.getFilteredRowModel().rows.length} page={visibleRows} gotoPage={table.setPageIndex} canNextPage={table.getCanNextPage()} canPreviousPage={table.getCanPreviousPage()} pageOptions={(manualPagination ? computedPageCount : table.getPageCount()) > 0 ? Array.from({
      length: manualPagination ? computedPageCount : table.getPageCount()
    }, (_, i) => i) : []} pageSize={table.getState().pagination.pageSize} pageIndex={table.getState().pagination.pageIndex} previousPage={table.previousPage} nextPage={table.nextPage} setPageSize={table.setPageSize} manualPageSize={pageSizeOptions} />}

      {enableColumnConfig && columnConfigKey && <ConfigModal show={showColumnConfig} onHide={() => setShowColumnConfig(false)} centered>
          <ConfigModalBody onDragOver={handleAutoScroll}>
            <ConfigHeader>
              <ConfigTitle>Configurar colunas</ConfigTitle>
              <ConfigCloseButton type="button" onClick={() => setShowColumnConfig(false)}>
                ×
              </ConfigCloseButton>
            </ConfigHeader>
            <ConfigList ref={configListRef} onDragOver={handleAutoScroll}>
              {table.getAllLeafColumns().map(column => {
            const canHide = column.getCanHide?.() ?? true;
            const canPin = column.getCanPin?.() ?? true;
            return <ConfigRow key={column.id}>
                      <DropOverlay className={dragOverId === column.id ? 'is-active' : ''}>
                        Soltar aqui
                      </DropOverlay>
                      <ConfigName>
                        <input type="checkbox" checked={column.getIsVisible()} disabled={!canHide} onChange={column.getToggleVisibilityHandler()} />
                        <span>{getColumnLabel(column)}</span>
                      </ConfigName>
                      <ConfigActions>
                        <ConfigGroup>
                          <ConfigGroupLabel>Fixar</ConfigGroupLabel>
                          <ConfigInline>
                            <ConfigMiniButton type="button" disabled={!canPin || column.getIsPinned() === 'left'} onClick={() => column.pin('left')}>
                              Esquerda
                            </ConfigMiniButton>
                            <ConfigMiniButton type="button" disabled={!canPin || !column.getIsPinned()} onClick={() => column.pin(false)}>
                              Soltar
                            </ConfigMiniButton>
                            <ConfigMiniButton type="button" disabled={!canPin || column.getIsPinned() === 'right'} onClick={() => column.pin('right')}>
                              Direita
                            </ConfigMiniButton>
                          </ConfigInline>
                        </ConfigGroup>
                        <ConfigGroup>
                          <ConfigGroupLabel>Ordenar</ConfigGroupLabel>
                          <ConfigInline>
                            <ConfigMiniButton type="button" onClick={() => moveColumn(column.id, 'up')}>
                              ↑
                            </ConfigMiniButton>
                            <ConfigMiniButton type="button" onClick={() => moveColumn(column.id, 'down')}>
                              ↓
                            </ConfigMiniButton>
                          </ConfigInline>
                        </ConfigGroup>
                      </ConfigActions>
                      <DragHandle
                        draggable
                        onDragStart={(event) => handleDragStart(event, column.id)}
                        onDragOver={(event) => handleDragOver(event, column.id)}
                        onDragLeave={(event) => handleDragLeave(event, column.id)}
                        onDrop={(event) => handleDrop(event, column.id)}
                        title="Arraste para reordenar"
                      >
                        ⠿
                      </DragHandle>
                    </ConfigRow>;
          })}
            </ConfigList>
            <ConfigFooter>
              <ConfigMiniButton type="button" onClick={handleResetColumnConfig}>
                Restaurar padrao
              </ConfigMiniButton>
              {isListagemConfigAtiva
                ? <ConfigHint>Use &quot;Salvar configuracao&quot; nos filtros para persistir o layout</ConfigHint>
                : <ConfigPrimaryButton type="button" onClick={handleSaveColumnConfig}>
                    Salvar layout
                  </ConfigPrimaryButton>}
            </ConfigFooter>
          </ConfigModalBody>
        </ConfigModal>}

      {enableExportExcel && <ConfigModal show={showExportConfig} onHide={() => setShowExportConfig(false)} centered>
          <ConfigModalBody>
            <ConfigHeader>
              <ConfigTitle>Exportar colunas</ConfigTitle>
              <ConfigCloseButton type="button" onClick={() => setShowExportConfig(false)}>
                x
              </ConfigCloseButton>
            </ConfigHeader>
            <ConfigList>
              {table.getVisibleLeafColumns().map(column => (
                <ConfigRow key={`export-${column.id}`}>
                  <ConfigName>
                    <input
                      type="checkbox"
                      checked={exportColumnIds.includes(column.id)}
                      onChange={() => handleToggleExportColumn(column.id)}
                    />
                    <span>{getColumnLabel(column)}</span>
                  </ConfigName>
                </ConfigRow>
              ))}
            </ConfigList>
            <ConfigFooter>
              <ConfigMiniButton type="button" onClick={() => setShowExportConfig(false)}>
                Cancelar
              </ConfigMiniButton>
              <ConfigPrimaryButton type="button" disabled={exportColumnIds.length === 0} onClick={handleExportConfirm}>
                Exportar
              </ConfigPrimaryButton>
            </ConfigFooter>
          </ConfigModalBody>
        </ConfigModal>}
    </div>;
};

TanStackTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    accessorKey: PropTypes.string,
    cell: PropTypes.func,
    size: PropTypes.number,
    enableSorting: PropTypes.bool,
    enableCopy: PropTypes.bool,
    copyValue: PropTypes.func
  })).isRequired,
  data: PropTypes.array.isRequired,
  onRowClick: PropTypes.func,
  isSortable: PropTypes.bool,
  withPagination: PropTypes.bool,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  initialState: PropTypes.object,
  className: PropTypes.string,
  enableRowColors: PropTypes.bool,
  loading: PropTypes.bool,
  enableRowSelection: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  getRowId: PropTypes.func,
  manualPagination: PropTypes.bool,
  totalItems: PropTypes.number,
  onPaginationChange: PropTypes.func,
  controlledPagination: PropTypes.shape({
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number
  }),
  enableColumnConfig: PropTypes.bool,
  columnConfigKey: PropTypes.string,
  enableExportExcel: PropTypes.bool,
  exportFileName: PropTypes.string,
  rowTitleGetter: PropTypes.func,
  enableColumnResizing: PropTypes.bool,
  columnResizeMode: PropTypes.oneOf(['onEnd', 'onChange']),
  columnResizeDirection: PropTypes.oneOf(['ltr', 'rtl']),
  enableCardView: PropTypes.bool,
  defaultViewMode: PropTypes.oneOf(['table', 'cards']),
  renderCard: PropTypes.func,
  cardEmptyMessage: PropTypes.string,
  enableCopy: PropTypes.bool,
  onColumnLayoutChange: PropTypes.func,
  // (columnSizing: { [columnId]: px }) => void — chamado quando o usuário redimensiona uma coluna.
  onColumnSizingChange: PropTypes.func,
  // Lado do botão de copiar na célula: 'start' deixa o fim livre para ações da própria célula (ex.: lápis de edição).
  copyButtonPosition: PropTypes.oneOf(['start', 'end'])
}; // Estilos

const TableContainer = styled.div`
  overflow: auto;
  max-height: ${props => props.withPagination ? '600px' : 'calc(100vh - 200px)'};
  border: 1px solid ${colorBorder};
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: ${colorWhite};
  position: relative;

  // Scrollbar personalizada
  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 0;
    border: 1px solid #f1f1f1;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  &::-webkit-scrollbar-corner {
    background: #f1f1f1;
  }

  @media (max-width: 768px) {
    max-height: none;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
  }
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  white-space: nowrap;
  background: ${colorWhite};
  table-layout: fixed;

  @media (max-width: 768px) {
    width: max-content;
    min-width: 100%;
    table-layout: auto;
  }
`;
const TableHeader = styled.thead`
  background-color: ${colorBackgroundBody};
  position: sticky;
  top: 0;
  z-index: 10;
`;
const TableHeaderCell = styled.th`
  position: relative;
  padding: 4px 6px;
  box-sizing: border-box;
  text-align: left;
  font-weight: 600;
  color: ${colorText};
  border-bottom: 2px solid ${colorBorder};
  background-color: ${colorBackgroundBody};
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  transition: background-color 0.2s ease;
  min-width: ${props => props.width || 'auto'};
  top: 0;

  &:hover {
    background-color: ${props => props.sortable ? colorHover : colorBackgroundBody};
  }

  ${props => props.sticky && `
    position: sticky;
    left: 0;
    z-index: 20;
    background-color: ${colorBackgroundBody};
    background-clip: padding-box;

    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.1) 20%, rgba(0, 0, 0, 0.1) 80%, transparent);
      box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
    }
  `}
`;
const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-right: ${props => props.resizable ? '8px' : '0'};
`;
const SortIcon = styled.span`
  font-size: 12px;
  color: ${colorAccent};
  font-weight: bold;
`;
const ColumnResizer = styled.div`
  position: absolute;
  top: 0;
  right: -4px;
  height: 100%;
  width: 8px;
  cursor: col-resize;
  touch-action: none;
  user-select: none;
  z-index: 30;

  &::before {
    content: '';
    position: absolute;
    top: 15%;
    bottom: 15%;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
    background: rgba(35, 57, 128, 0.2);
    transition: background 0.2s ease;
  }

  &:hover::before,
  &.isResizing::before {
    background: ${colorAccent};
  }
`;
const TableBody = styled.tbody``;
const TableRow = styled.tr`
  transition: background-color 0.2s ease;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};

  &:hover {
    background-color: ${colorHover} !important;
  }

  &:nth-child(even) {
    background-color: #f8f9fa;
  }
  &:nth-child(odd) {
    background-color: #fff;
  }

  &:nth-child(even):hover,
  &:nth-child(odd):hover {
    background-color: ${colorHover} !important;
  }
`;
const TableCell = styled.td`
  padding: 4px 6px;
  box-sizing: border-box;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${props => props.maxWidth || 'none'};

  ${props => props.sticky && `
    position: sticky;
    left: 0;
    z-index: 10;
    background-color: ${colorWhite};
    background-clip: padding-box;

    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.1) 20%, rgba(0, 0, 0, 0.1) 80%, transparent);
      box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
    }
  `}
`;
const CellInner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $copyStart }) => ($copyStart ? '0' : '6px')};
  min-width: 0;

  &:hover button {
    opacity: 1;
  }
`;
const CellValueWrap = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;
const CopyCellButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, color 0.15s ease;
  flex: 0 0 auto;

  &:hover {
    color: ${colorAccent};
  }

  &:focus {
    opacity: 1;
    outline: none;
  }

  /* No início da célula o botão não ocupa espaço até o mouse passar (o texto segue alinhado ao cabeçalho). */
  ${({ $start }) => $start && `
    width: 0;
    overflow: hidden;
    transition: opacity 0.15s ease, color 0.15s ease, width 0.15s ease, margin 0.15s ease;
  `}
  ${CellInner}:hover & ,
  &:focus {
    ${({ $start }) => ($start ? 'width: 18px; margin-right: 6px;' : '')}
  }
`;
const EmptyCell = styled.td`
  padding: 40px 16px;
  text-align: center;
  border-bottom: 1px solid ${colorBorder};
`;
const EmptyMessage = styled.div`
  color: ${colorText};
  font-style: italic;
`;
const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: ${colorBackgroundBody};
  border-top: 1px solid ${colorBorder};
`;
const PaginationInfo = styled.span`
  color: ${colorText};
  font-size: 14px;
`;
const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const PaginationButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${colorBorder};
  background-color: ${colorWhite};
  color: ${colorText};
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover:not(:disabled) {
    background-color: ${colorAccent};
    color: ${colorWhite};
    border-color: ${colorAccent};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const PageSizeSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${colorBorder};
  border-radius: 0;
  background-color: ${colorWhite};
  color: ${colorText};
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colorAccent};
    box-shadow: 0 0 0 2px rgba(35, 57, 128, 0.2);
  }
`;
const ConfigToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;
const ToolbarGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
const ViewModeSwitch = styled.div`
  display: inline-flex;
  border: 2.5px solid ${colorBorder};
  border-radius: 8px;
  overflow: hidden;
  background: ${colorWhite};
`;
const ViewModeButton = styled.button`
  border: none;
  background: ${props => props.$active ? colorAccent : colorWhite};
  color: ${props => props.$active ? colorWhite : colorText};
  width: 42px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const ConfigButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${colorBorder};
  background: ${colorWhite};
  color: ${colorText};
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    border-color: ${colorAccent};
    color: ${colorAccent};
  }
`;
const ConfigHint = styled.span`
  font-size: 12px;
  color: ${colorGreen};
`;
const ConfigModal = styled(BootstrapModal)`
  .modal-dialog {
    max-width: 720px;
  }

  .modal-content {
    border-radius: 8px;
    border: 1px solid ${colorBorder};
    padding: 18px 20px;
    background: ${colorWhite};
  }
`;
const ConfigModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ConfigHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ConfigTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  color: ${colorText};
`;
const ConfigCloseButton = styled.button`
  border: none;
  background: transparent;
  color: ${colorText};
  font-size: 18px;
  cursor: pointer;
  line-height: 1;
`;
const ConfigList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 60vh;
  overflow: auto;
`;
const ConfigRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px solid ${colorBorder};
  border-radius: 8px;
  background: ${colorBackgroundBody};
  position: relative;
  transition: transform 0.15s ease;
`;
const ConfigName = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: ${colorText};
  flex: 1;
  min-width: 160px;
`;
const ConfigActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;
const DragHandle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 1px dashed ${colorBorder};
  border-radius: 6px;
  font-size: 14px;
  color: ${colorText};
  cursor: grab;
`;
const DropOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 8px;
  border: 2px dashed ${colorAccent};
  background: rgba(35, 57, 128, 0.08);
  color: ${colorAccent};
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;

  &.is-active {
    opacity: 1;
  }
`;
const ConfigGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const ConfigGroupLabel = styled.span`
  font-size: 11px;
  color: ${colorText};
  opacity: 0.7;
`;
const ConfigInline = styled.div`
  display: inline-flex;
  gap: 6px;
`;
const ConfigMiniButton = styled.button`
  padding: 4px 8px;
  border: 1px solid ${colorBorder};
  background: ${colorWhite};
  color: ${colorText};
  font-size: 11px;
  border-radius: 4px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const ConfigPrimaryButton = styled(ConfigMiniButton)`
  background: ${colorAccent};
  color: ${colorWhite};
  border-color: ${colorAccent};
`;
const ConfigFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;
const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const CardItem = styled.div``;
const CardEmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: ${colorText};
  font-style: italic;
  border: 1px solid ${colorBorder};
  background: ${colorWhite};
`;
export default TanStackTable;
